import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { PropsWithChildren, Suspense, useEffect, useMemo, useState } from 'react';
import { useScreenSize } from 'src/modules/common/hooks/useScreenSize.ts';
import { RuntimeContext, RuntimeContextProps } from 'src/runtimeContext.ts';
import { MOBILE_WIDTH, revitSharedParameterFile } from 'src/modules/common/constants.ts';
import { getModalRoot } from 'src/utils/getModalRoot.ts';
import { RevitConfig } from 'src/types/RevitConfig';
import { UserSettings } from 'src/types/UserSettings';
import { UserLocalOptionsClientInterface } from 'src/common/UserLocalOptionsClient/UserLocalOptionsClientInterface';

type Props = {
    userSettings: UserSettings;
    rootElement: HTMLElement;
    userOptionsClient: UserLocalOptionsClientInterface;
} & PropsWithChildren;

export const RuntimeContextProvider = ({
    userSettings, rootElement, userOptionsClient, children,
}: Props) => {
    const collator = useMemo(() => {
        const language = userSettings.currentLocale.substring(0, 2);

        return new Intl.Collator(language);
    }, [userSettings.currentLocale]);

    const [revitConfig, setRevitConfig] = useState<RevitConfig | null>(
        JSON.parse(localStorage.getItem('revitConfig') || 'null'),
    );

    const { width } = useScreenSize();
    const value: RuntimeContextProps = useMemo(() => ({
        isMobile: width <= MOBILE_WIDTH,
        collator,
        isRevitPlugin: revitConfig !== null,
        isRevit6WayFiltered: revitConfig !== null && revitConfig.platform === 'MCREVIT',
        userOptionsClient,
        ...getModalRoot(rootElement),
    }), [width, collator, rootElement, revitConfig, userOptionsClient]);

    useEffect(() => {
        if (window.eoapi && window.eoapi.extInvoke && window.eoapi.isEOWebBrowser?.()) {
            window.eoapi.extInvoke('MCPluginWebAppLoaded', {
                MCPluginWebAppInsertProduct: (
                    version: string,
                    platform: string,
                ) => setRevitConfig({ version, platform }),
            });
            window.eoapi.extInvoke('MCPluginRevitSharedParameterFile', revitSharedParameterFile);
        }
    }, []);

    return (
        <RuntimeContext.Provider value={value}>
            <Suspense fallback={<SkeletonMainLayout/>}>{children}</Suspense>
        </RuntimeContext.Provider>
    );
};
